import { useEffect, useMemo, useRef, useState } from "react";
import { KeyRound, Loader2, LockKeyhole, ShieldCheck } from "lucide-react";
import { ToastContainer } from "react-toastify";

const PIN_LENGTH = 6;

const parseUserRole = (userRole = "") => {
  const [fkuserid = "", roleName = ""] = String(userRole || "").split("|");

  return {
    fkuserid: fkuserid.trim(),
    roleName: roleName.trim(),
  };
};

export default function PosPinScreen({
  sessionUser,
  onValidate,
  loading = false,
}) {
  const [pin, setPin] = useState(Array(PIN_LENGTH).fill(""));
  const inputRefs = useRef([]);

  const { fkuserid, roleName } = useMemo(
    () => parseUserRole(sessionUser?.userRole),
    [sessionUser?.userRole]
  );

  useEffect(() => {
    setTimeout(() => {
      inputRefs.current?.[0]?.focus();
    }, 100);
  }, []);

  const pinValue = pin.join("");
  const isPinComplete = pinValue.length === PIN_LENGTH;

  const handleChange = (value, index) => {
    const cleanValue = value.replace(/\D/g, "").slice(0, 1);

    const updatedPin = [...pin];
    updatedPin[index] = cleanValue;

    setPin(updatedPin);

    if (cleanValue && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "Enter" && isPinComplete && !loading) {
      onValidate(pinValue);
    }
  };

  const handleSubmit = () => {
    if (!isPinComplete || loading) return;
    onValidate(pinValue);
  };

  return (
    <>
    <ToastContainer/>
  
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 montserrat-font-1">
      <div className="w-full max-w-[520px] bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-emerald-50 px-7 py-7 border-b border-emerald-100">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center shadow-md">
              <LockKeyhole size={26} />
            </div>

            <div>
              <h1 className="text-xl font-bold text-gray-800">
                POS Access Required
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Enter your 6 digit drawer PIN to continue
              </p>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="px-7 pt-6">
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 space-y-2">
            <div className="flex justify-between gap-4 text-sm">
              <span className="text-gray-500">User ID</span>
              <span className="font-semibold text-gray-800">
                {sessionUser?.userid || "-"}
              </span>
            </div>

            <div className="flex justify-between gap-4 text-sm">
              <span className="text-gray-500">FK User ID</span>
              <span className="font-semibold text-gray-800">
                {fkuserid || "-"}
              </span>
            </div>

            <div className="flex justify-between gap-4 text-sm">
              <span className="text-gray-500">Role</span>
              <span className="font-semibold text-emerald-700">
                {roleName || "-"}
              </span>
            </div>

            <div className="flex justify-between gap-4 text-sm">
              <span className="text-gray-500">Name</span>
              <span className="font-semibold text-gray-800 text-right">
                {sessionUser?.username || "-"}
              </span>
            </div>
          </div>
        </div>

        {/* PIN Input */}
        <div className="px-7 py-7">
          <label className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-4">
            <KeyRound size={16} />
            Enter 6 digit PIN
          </label>

          <div className="grid grid-cols-6 gap-2 sm:gap-3">
            {pin.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                disabled={loading}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="h-14 sm:h-16 rounded-2xl border border-gray-300 text-center text-xl sm:text-2xl font-bold outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-gray-100"
              />
            ))}
          </div>

          <button
            onClick={handleSubmit}
            disabled={!isPinComplete || loading}
            className={`mt-6 w-full rounded-2xl py-4 font-bold flex items-center justify-center gap-2 transition-all ${
              isPinComplete && !loading
                ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-md"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Validating...
              </>
            ) : (
              <>
                <ShieldCheck size={18} />
                Unlock POS
              </>
            )}
          </button>

          <p className="text-xs text-gray-400 text-center mt-4">
            POS will remain unlocked for this browser session.
          </p>
        </div>
      </div>
    </div>
    </>
  );
}